/**
 * Audio Playback
 *
 * Handles playback of voice notes.
 */

export interface PlaybackState {
  isPlaying: boolean
  currentTime: number
  duration: number
}

export interface PlaybackOptions {
  onTimeUpdate?: (time: number) => void
  onEnded?: () => void
  onError?: (error: Error) => void
}

let currentAudio: HTMLAudioElement | null = null
let currentBlobUrl: string | null = null

/**
 * Create an audio element from a blob
 */
export function createAudioFromBlob(blob: Blob): HTMLAudioElement {
  const url = URL.createObjectURL(blob)
  const audio = new Audio(url)
  return audio
}

/**
 * Create an audio element from a Uint8Array
 */
export function createAudioFromBytes(
  bytes: Uint8Array,
  mimeType: string = 'audio/webm'
): HTMLAudioElement {
  // Create a copy to ensure we have a proper ArrayBuffer
  const blob = new Blob([new Uint8Array(bytes)], { type: mimeType })
  return createAudioFromBlob(blob)
}

/**
 * Play an audio blob
 */
export async function playAudio(
  audioData: Blob | Uint8Array,
  options: PlaybackOptions = {}
): Promise<void> {
  // Stop any current playback
  stopAudio()

  const { onTimeUpdate, onEnded, onError } = options

  try {
    // Create a copy of Uint8Array to ensure proper ArrayBuffer
    const blob = audioData instanceof Blob
      ? audioData
      : new Blob([new Uint8Array(audioData)], { type: 'audio/webm' })

    currentBlobUrl = URL.createObjectURL(blob)
    currentAudio = new Audio(currentBlobUrl)

    if (onTimeUpdate) {
      currentAudio.ontimeupdate = () => {
        onTimeUpdate(currentAudio!.currentTime)
      }
    }

    currentAudio.onended = () => {
      onEnded?.()
      cleanupAudio()
    }

    currentAudio.onerror = () => {
      const error = new Error('Audio playback failed')
      onError?.(error)
      cleanupAudio()
    }

    await currentAudio.play()
    console.log('[Player] Started playback')
  } catch (error) {
    console.error('[Player] Failed to play:', error)
    onError?.(error instanceof Error ? error : new Error(String(error)))
    cleanupAudio()
    throw error
  }
}

/**
 * Stop current playback
 */
export function stopAudio(): void {
  if (currentAudio) {
    currentAudio.pause()
    currentAudio.currentTime = 0
    cleanupAudio()
    console.log('[Player] Stopped playback')
  }
}

/**
 * Pause current playback
 */
export function pauseAudio(): void {
  if (currentAudio && !currentAudio.paused) {
    currentAudio.pause()
  }
}

/**
 * Resume playback
 */
export async function resumeAudio(): Promise<void> {
  if (currentAudio && currentAudio.paused) {
    await currentAudio.play()
  }
}

/**
 * Seek to a specific time
 */
export function seekAudio(time: number): void {
  if (currentAudio) {
    currentAudio.currentTime = Math.max(0, Math.min(time, currentAudio.duration))
  }
}

/**
 * Get current playback state
 */
export function getPlaybackState(): PlaybackState | null {
  if (!currentAudio) return null

  return {
    isPlaying: !currentAudio.paused,
    currentTime: currentAudio.currentTime,
    duration: currentAudio.duration || 0,
  }
}

/**
 * Check if audio is currently playing
 */
export function isPlaying(): boolean {
  return currentAudio ? !currentAudio.paused : false
}

/**
 * Set playback volume (0-1)
 */
export function setVolume(volume: number): void {
  if (currentAudio) {
    currentAudio.volume = Math.max(0, Math.min(1, volume))
  }
}

/**
 * Format time in seconds to mm:ss
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Clean up audio resources
 */
function cleanupAudio(): void {
  if (currentBlobUrl) {
    URL.revokeObjectURL(currentBlobUrl)
    currentBlobUrl = null
  }
  currentAudio = null
}
